<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          title="Doctor Appointment Schedule"
          btnTitle="Add New"
          :showBtn="false"
          :showAddNew="false"
          :showExtraBtn="true"
          extraBtnTitle="List View"
          @onClickExtraBtn="navigateToListPage"
      />
    </div>
    <div class="my-2 px-2">
        <div class="row">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Doctor</label>
          <AsyncSelect
              placeholder="Select Doctor"
              v-model="contactProfileId"
              :api-service="fetchContactProfiles"
              :additional-query="{ roles: 'doctor,medical_officer', status: 'active' }"
              label="name"
              :reduce="name => name.id"
          />
        </div>
          <div class="col-sm-2 col-md-2 d-flex align-items-end mt-2 action-btn-setter">
            <button
                style="min-width: 64px;"
                @click="onClickToGo"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            >Go
            </button>
          </div>
        </div>
    </div>

    <div class="px-2">
    <div class="row align-items-center">
      <div class="col-md-3 col-12 d-flex align-items-start justify-content-start">
        <button
            @click="goToToday"
            class="btn btn-secondary btn-sm"
            :disabled="loading"
        >
          Today
        </button>
      </div>

      <div class="col-md-9 col-12 d-flex align-items-center gap-1">
        <button
            @click="navigatePrevious"
            class="btn btn-outline-primary btn-sm"
            :disabled="loading"
        >
          <i class="bi bi-chevron-left"></i> Previous
        </button>

        <div class="  text-center">
          <div class="badge bg-light text-dark fs-6 py-1 px-3">
            {{ currentPeriodLabel }}
          </div>
        </div>

        <button
            @click="navigateNext"
            class="btn btn-outline-primary btn-sm"
            :disabled="loading"
        >
          Next <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>
    </div>

    <div class="col-12">
      <div class="card shadow-sm border-0">
        <div class="card-body" style="min-height: 300px">
          <div class="table-responsive border rounded">
            <table class="table table-bordered align-middle text-center no-hover mb-0">
            <thead class="table-light">
              <tr>
                <th class="sticky-col">Doctor Details</th>
                <th
                    v-for="hour in hours"
                    :key="hour"
                    class="bg-light fw-semibold text-secondary"
                >
                  {{ formatHour(hour) }}
                </th>
              </tr>
              </thead>

              <tbody>
              <tr v-for="emp in tableItems" :key="emp.id">
                <td class="sticky-col text-start">
                  {{ emp.name }} ({{ emp.serial_no }})
                </td>

                <template v-for="hour in hours" :key="hour">
                  <td v-if="hour === getHour(emp.in_time)"
                      :colspan="getDuration(emp)"
                      class="bg-primary text-white fw-semibold text-center"
                      >
                     <div>
                       {{ emp.title }}
                     </div>
                  </td>

                  <td v-else-if="hour > getHour(emp.in_time) && hour < getHour(emp.out_time)"
                      style="display:none">
                  </td>

                  <td v-else></td>
                </template>
              </tr>
              <tr v-if="tableItems.length < 1">
                <td colspan="25">
                  No Data Found
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="px-2 position-absolute bottom-0 mt-4">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading"/>

  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import handleDropdowns from "@/services/modules/dropdowns";
import Pagination from '@/components/atom/Pagination'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from '@/services/modules/contact'

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const tableItems = ref([]);
const contactProfileId = ref('');
const currentDate = ref(new Date());
const params = ref({
  offset: 20,
});
const token = computed(() => localStorage.getItem('token'));

const {
  fetchDoctorAppointmentScheduleList
} = handleHrPayroll()
const { fetchContactProfiles } = handleContact()

const hours = computed(() => {
  return Array.from({ length: 24 }, (_, i) => i)
})

const formatHour = (hour) => {
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 === 0 ? 12 : hour % 12
  return `${displayHour} ${period}`
}

const getHour = (time) => {
  return parseInt(time.split(':')[0])
}

function navigateToListPage() {
  router.push({name: `doctors-appointment-schedule-list`, params: route.params, query: route.query});
}

const getDuration = (emp) => {
  return getHour(emp.out_time) - getHour(emp.in_time)
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getDoctorSchedule();
}

const currentPeriodLabel = computed(() => {
  const date = currentDate.value;

  return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
  });

  return '';
});

const onClickToGo = () => {
  getDoctorSchedule();
}

const getDoctorSchedule = async () => {
  const query = getQuery();
  loading.value = true
  tableItems.value = []

  await fetchDoctorAppointmentScheduleList(query).then(res => {
    if (!res.status){
      store.commit("resetPagination")
      return
    }
    tableItems.value = res.data.data;
    setPagination(res.data)
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}`;
  const date = currentDate.value;
  let startDate, endDate;
  startDate = formatDateForAPI(date);
  endDate = formatDateForAPI(date);

  query += '&start_date=' + startDate;
  query += '&end_date=' + endDate;
  query += '&offset=' + params.value.offset;
  if (contactProfileId.value) query +=  '&contact_profile_id=' + contactProfileId.value
  if (route.query.page) query += '&page=' + route.query.page;
  return query;
}

const updateRouteQuery = () => {
  const date = currentDate.value;
  let startDate, endDate;
  startDate = formatDateForAPI(date);
  endDate = formatDateForAPI(date);
  router.push({
    query: {
      ...route.query,
      start: startDate,
      end: endDate,
    }
  });
};

const navigatePrevious = () => {
  const date = new Date(currentDate.value);
  date.setDate(date.getDate() - 1);
  currentDate.value = date;
  updateRouteQuery();
  getDoctorSchedule();
};

const navigateNext = () => {
  const date = new Date(currentDate.value);
  date.setDate(date.getDate() + 1);

  currentDate.value = date;
  updateRouteQuery();
  getDoctorSchedule();
};

const goToToday = () => {
  currentDate.value = new Date();
  updateRouteQuery();
  getDoctorSchedule();
};

const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

onMounted(() => {
  Promise.all([
    getDoctorSchedule()
  ])
})

</script>

<style scoped>
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background: white;
}

.table > tbody > tr:hover {
   --bs-table-accent-bg: inherit;
}

.shift-header {
  min-width: 120px;
}
</style>